import React, {useEffect, useMemo } from "react";

import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import * as constants from "../../constants/authConstants";
import * as routeConstants from "../../constants/routeConstants";

import { login, logout } from "../../store/auth/auth-actions";
import { getMyProfile } from "../../store/auth/auth-actions";

import {getAbortSignal} from '../../util/signalHelper';

//make sure you understand everything properly before editing this file
const Authentication = props => {
  const dispatch = useDispatch();
  const navigate = useNavigate(); 
  const location = useLocation();
  const auth = useSelector((state) => state.auth);
  const isAuthenticating = location.pathname === routeConstants.ROUTE_AUTH;
  
  let token = auth.token;
  let expiresAt = useMemo(() => auth.expiresAt ? new Date(auth.expiresAt) : null, [auth.expiresAt]);
  let callLogin = false;

  const isExpired = expiresAt && !(expiresAt.getTime() - new Date().getTime());

  useEffect(() => {
    console.count("Authentication -> useEffect login");
    if (!callLogin) return;
    if (isExpired) return;
    if (!token) return;
    console.count("Authentication -> useEffect login: dispatching");
    
    dispatch(login(token, expiresAt));

  }, [dispatch, token, expiresAt, callLogin, isExpired]);

  useEffect(() => {
    const [signal, controller] = getAbortSignal();

    console.count("Authentication -> useEffect profile");
    if (!auth.token) return;
    if (isExpired) return;
    
    console.count("Authentication -> useEffect profile: getMyProfile");
    dispatch(getMyProfile(auth.token, signal));

    //cleanup functions appear to be a necessity nowadays for future react versions regarding improved performance called "reusable state"
    //https://reactjs.org/docs/strict-mode.html#ensuring-reusable-state
    return () => {
      console.count("Authentication -> cancel getMyProfile");
      controller.abort();
    }
  }, [dispatch, auth.token, isExpired]);

  useEffect(() => {
    console.log("Authentication -> useEffect expired");
    if (!isExpired) return;
    console.count("Authentication -> useEffect expired: logging out");
    dispatch(logout());
  }, [dispatch, isExpired]);

  useEffect(() => {
    const redirectToLocation = window.localStorage.getItem(constants.KEY_REDIRECT_TO_AFTER_LOGIN);

    if (redirectToLocation && auth.isLoggedIn && !isAuthenticating) {
      window.localStorage.removeItem(constants.KEY_REDIRECT_TO_AFTER_LOGIN);
      console.log(`Authentication -> useEffect: redirecting to: ${redirectToLocation}`);
      navigate(redirectToLocation);
    } 
  }, [auth.isLoggedIn, isAuthenticating, navigate]);

  if (auth.doRedirectLogout) {
    console.log("Authentication -> directing to logout url");
    window.location.href = constants.LOGOUT_URL;
    return (<></>);
  }

  //default behaviour
  if (auth.isLoggedIn && !isAuthenticating) {    
    //load content, useEffect will handle redirect if set
    //console.log("Authentication -> user is logged in, route is not authenticating");
    return (props.children);
  }

  //Check for token and expiresIn in url parameters
  if (window.location.href.includes(constants.KEY_ACCESS_TOKEN_PREFIXED)) {
    //get token from url
    const urlParams = new URLSearchParams(window.location.hash.substring(1));
    token = urlParams.get(constants.KEY_ACCESS_TOKEN);

    //get expiredIn from url
    if (window.location.href.includes(constants.KEY_EXPIRES_IN)) {
      const expiresIn = urlParams.get(constants.KEY_EXPIRES_IN);
      expiresAt = new Date(new Date().getTime() + (1000 * expiresIn));
    }
  }

  //if there is no token and the path is /auth -> get token (=redirect to login url at Genesys)
  if (!token && isAuthenticating) {
    console.log("Authentication -> will direct to login url");
    window.location.href= constants.LOGIN_URL;
    return (<></>);
  }

  //not being logged in will trigger log in in useEffect -> set redirect page
  if (!auth.isLoggedIn && !isAuthenticating) {
    window.localStorage.setItem(constants.KEY_REDIRECT_TO_AFTER_LOGIN, location.pathname);
  }

  //make sure the useEffect logs you in after page render
  callLogin = true;

  //if you arrive here this means that the token is set.
  //The login useEffect will also be called
  //It is necessary to move away from the /auth endpoint -> go home
  if (isAuthenticating) {
    console.log("Authentication -> token is set, page is authenticating, move to default route");
    return (<Navigate replace to={routeConstants.ROUTE_DEFAULT} />);
  }
  
  //load children -> when logged out and no token is set in the local storage
  console.log("Authentication -> no token is set, user is logged out");
  return (props.children);
}

export default Authentication;